import { memo } from 'react';
import { Cog, ArrowRight, Hash, Clock3, RefreshCcw, Link2, Layers, Zap, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/Tooltip';
import {
  formatDurationHuman,
  formatDurationVsEta,
  durationMsToSeconds,
  relativeFromNow,
  activityFreshness,
  shortId,
  progressRatio,
  computeDeltaSeconds,
  formatDeltaSeconds,
} from './format';
import { useRunningDuration } from '@/hooks/useRunningDuration';
import { STATUS_BORDER_TOKEN, STATUS_LABEL, type TaskUI } from './types';

interface TaskCardProps {
  task: TaskUI;
  onClick?: (task: TaskUI) => void;
  /** Quando true, card aparece em estado "drag preview" (opacidade reduzida etc). */
  dragging?: boolean;
}

const FRESHNESS_DOT: Record<ReturnType<typeof activityFreshness>, string> = {
  fresh: 'bg-raios-success',
  recent: 'bg-raios-warning',
  stale: 'bg-raios-muted',
};

function TaskCardInner({ task, onClick, dragging = false }: TaskCardProps) {
  const handlerLabel = task.handler.replace(/^@/, '');
  const target = task.target_slug;

  // Tempo decorrido pra task running (calculado live; pra outros estados usa duration_ms convertido).
  // G4 F1 (D-G4-F): backend canonical é duration_ms (G3 PR-P1 dropou duration_seconds).
  // P1.15 Z2 (D-G4-G): useRunningDuration calcula runtime quando running + duration_ms NULL
  // (re-render a cada 1s); em terminal retorna duration_ms do backend direto.
  const effectiveDurationMs = useRunningDuration(task);
  const elapsed = durationMsToSeconds(effectiveDurationMs);
  const eta = task.estimated_duration_seconds;
  // P1.15 Z3: delta canonical = duration_ms - estimated_seconds*1000, normalizado
  // pra segundos. Frontend computa direto pra não depender de campo backend
  // que pode não existir. backendToTaskUI já alimenta task.delta_*; aqui
  // re-derivamos pra terminal (consistência) e exibimos +/- explícito.
  const delta = computeDeltaSeconds(task.duration_ms, eta);
  const ratio = task.status === 'running' ? progressRatio(elapsed, eta) : null;
  const freshness = activityFreshness(task.updated_at);

  // Display da linha de tempo, depende do estado.
  let timingLine: string;
  if (task.status === 'pending') {
    timingLine = `ETA ${formatDurationHuman(eta)}`;
  } else if (task.status === 'running') {
    timingLine = formatDurationVsEta(elapsed, eta);
  } else if (task.status === 'done' || task.status === 'failed' || task.status === 'cancelled') {
    const real = formatDurationHuman(elapsed);
    if (eta !== null) {
      timingLine = `${real} · ETA ${formatDurationHuman(eta)}`;
    } else {
      timingLine = real;
    }
  } else {
    timingLine = `ETA ${formatDurationHuman(eta)}`;
  }

  // Cor do delta: negativo = mais rápido (success); positivo = atrasou (warning).
  const deltaColor = delta === null
    ? ''
    : delta < 0
      ? 'text-raios-success-text'
      : delta > 0
        ? 'text-raios-warning-text'
        : 'text-raios-muted';

  const handleClick = () => onClick?.(task);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(task);
    }
  };

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={`Task ${task.title}, status ${STATUS_LABEL[task.status]}`}
      className={cn(
        'group relative flex flex-col gap-2 rounded-md bg-raios-card border border-raios-elevated/40 border-l-[3px] p-3 cursor-pointer',
        'transition-[transform,box-shadow,border-color] duration-150',
        'hover:shadow-card-hover hover:-translate-y-0.5 hover:border-raios-elevated/70',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/60',
        STATUS_BORDER_TOKEN[task.status],
        dragging && 'opacity-50 ring-2 ring-raios-accent/60',
      )}
      data-status={task.status}
      data-task-id={task.id}
    >
      {/* Header: handler › target_slug + badge "órfã" (P1.13 F7) */}
      <header className="flex items-center gap-1.5 text-[11px] font-medium text-raios-text-secondary min-w-0">
        <Cog className="h-3 w-3 shrink-0 text-raios-muted" aria-hidden />
        <span className="truncate">{handlerLabel}</span>
        {target && (
          <>
            <ArrowRight className="h-3 w-3 shrink-0 text-raios-muted" aria-hidden />
            <span className="truncate text-raios-text">{target}</span>
          </>
        )}
        {/* P1.13 F7 — Indicator de task órfã (sub-task com FK canônica NULL).
            Sinaliza que B1 (#415) não rodou pra essa task — legacy ou bug. */}
        {task.is_orphan && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="ml-auto inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-medium bg-raios-warning/15 text-raios-warning-text border border-raios-warning/30 shrink-0"
                aria-label="Task órfã: sub-task com FK canônica NULL"
                data-testid="orphan-badge"
              >
                <AlertTriangle className="h-2.5 w-2.5" aria-hidden />
                órfã
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              Sub-task sem demand_id ou pipeline_run_id. Backend B1 (#415) propaga
              FKs do pai automaticamente — órfãs remanescentes são legacy ou bug
              residual a investigar.
            </TooltipContent>
          </Tooltip>
        )}
      </header>

      {/* Title */}
      <h3 className="text-sm font-medium text-raios-text leading-snug line-clamp-2">
        {task.title}
      </h3>

      {/* Refs row — só renderiza se algum link existe */}
      {(task.demand_id || task.pipeline_run_id || task.session_id) && (
        <div className="flex items-center gap-2 text-[11px] text-raios-text-secondary flex-wrap">
          {task.demand_id && (
            <span className="inline-flex items-center gap-0.5">
              <Link2 className="h-3 w-3" aria-hidden />
              demand {shortId(task.demand_id, '#')}
            </span>
          )}
          {task.pipeline_run_id && (
            <span className="inline-flex items-center gap-0.5">
              <Layers className="h-3 w-3" aria-hidden />
              pipeline {shortId(task.pipeline_run_id, '#')}
              {task.pipeline_step_id && <span className="text-raios-muted">· {task.pipeline_step_id}</span>}
            </span>
          )}
          {task.session_id && !task.pipeline_run_id && (
            <span className="inline-flex items-center gap-0.5">
              <Zap className="h-3 w-3" aria-hidden />
              ses {shortId(task.session_id, '#').slice(1)}
            </span>
          )}
        </div>
      )}

      {/* Progress bar (só running com ETA conhecido) */}
      {ratio !== null && (
        <div className="h-0.5 w-full bg-raios-elevated/40 rounded-full overflow-hidden" aria-hidden>
          <div
            className="h-full bg-raios-accent transition-[width] duration-300"
            style={{ width: `${Math.round(ratio * 100)}%` }}
          />
        </div>
      )}

      {/* Footer: timing + retry + activity */}
      <footer className="flex items-center gap-2 text-[11px] text-raios-text-secondary mt-auto pt-1 min-w-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1 truncate">
              <Clock3 className="h-3 w-3 shrink-0" aria-hidden />
              <span className="truncate">{timingLine}</span>
              {delta !== null && task.status !== 'running' && (
                <span
                  className={cn('ml-0.5 tabular-nums', deltaColor)}
                  data-testid="task-card-delta"
                  aria-label={delta > 0 ? `Atrasou ${formatDurationHuman(delta)} vs ETA` : delta < 0 ? `Mais rápido ${formatDurationHuman(Math.abs(delta))} vs ETA` : 'Bateu o ETA exato'}
                >
                  {formatDeltaSeconds(delta)}
                </span>
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            {eta === null
              ? 'Sem ETA registrado'
              : `ETA ${formatDurationHuman(eta)} · real ${formatDurationHuman(elapsed)}${delta === null ? '' : ` · delta ${formatDurationHuman(delta)}`}`}
          </TooltipContent>
        </Tooltip>

        {task.retry_count > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-0.5 text-raios-warning-text">
                <RefreshCcw className="h-3 w-3" aria-hidden />
                {task.retry_count}/{task.max_retries}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">Retries</TooltipContent>
          </Tooltip>
        )}

        <span className="ml-auto inline-flex items-center gap-1.5 text-raios-muted">
          <span className={cn('h-1.5 w-1.5 rounded-full', FRESHNESS_DOT[freshness])} aria-hidden />
          <span>{relativeFromNow(task.updated_at)}</span>
        </span>
      </footer>

      {/* Short ID em corner-bottom-left, sempre visível em hover */}
      <span
        className="absolute bottom-1 right-1.5 text-[9px] font-mono text-raios-muted opacity-0 group-hover:opacity-100 transition-opacity"
        aria-hidden
      >
        <Hash className="inline h-2.5 w-2.5" />{typeof task.id === 'string' ? task.id.slice(0, 6) : '—'}
      </span>
    </article>
  );
}

/**
 * Comparação shallow estável: re-render só se id, status, updated_at, retry_count, ou duration mudou.
 * Evita renders cascata ao realtime entregar updates noutras tasks.
 */
function areEqual(prev: TaskCardProps, next: TaskCardProps): boolean {
  if (prev.dragging !== next.dragging) return false;
  if (prev.onClick !== next.onClick) return false;
  const a = prev.task;
  const b = next.task;
  return (
    a.id === b.id &&
    a.status === b.status &&
    a.updated_at === b.updated_at &&
    a.retry_count === b.retry_count &&
    a.duration_ms === b.duration_ms &&
    a.estimated_duration_seconds === b.estimated_duration_seconds &&
    a.title === b.title &&
    a.is_orphan === b.is_orphan
  );
}

export const TaskCard = memo(TaskCardInner, areEqual);
TaskCard.displayName = 'TaskCard';
